import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  ThemeProvider,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { sanitizeRichHtml } from '../../utils/sanitizeRichHtml';
import { useProject } from '../../context/ProjectContext';
import indService from '../services/ind.service';
import indTheme from '../utils/indTheme';

// Clone of sap/components/SAPSectionHistory.jsx for IND sections.
const INDSectionHistory = () => {
  const { id: sectionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('projectId') || queryParams.get('project');

  const { currentProject, loading: projectContextLoading, error: projectContextError, loadProject } = useProject();
  const actualProjectId = currentProject?._id || projectIdFromUrl;

  const [section, setSection] = useState(null);
  const [versions, setVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (projectIdFromUrl && (!currentProject || currentProject._id !== projectIdFromUrl)) {
      loadProject(projectIdFromUrl);
    }
  }, [projectIdFromUrl, currentProject, loadProject]);

  const fetchSectionAndVersions = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const sectionData = await indService.getSection(sectionId, actualProjectId);
      setSection(sectionData);
      const versionsData = await indService.getVersions(sectionId, actualProjectId);
      setVersions(Array.isArray(versionsData?.versions) ? versionsData.versions : []);
    } catch (err) {
      console.error('Error fetching IND section history:', err);
      setError(err.message || 'Failed to load IND section history');
    } finally {
      setIsLoading(false);
    }
  }, [sectionId, actualProjectId]);

  useEffect(() => {
    if (projectContextLoading) return;
    if (sectionId && actualProjectId) {
      fetchSectionAndVersions();
    } else if (!sectionId && !actualProjectId) {
      setError('No section ID or project selected.');
      setIsLoading(false);
    } else if (sectionId && !actualProjectId) {
      setError('Please select a project to view its IND section history.');
      setIsLoading(false);
    }
  }, [sectionId, actualProjectId, projectContextLoading, fetchSectionAndVersions]);

  const handleBack = () => {
    navigate(`/clinical-intake?project=${actualProjectId || ''}&tab=ind`);
  };

  const backButton = (
    <Button variant="outlined" startIcon={<BackIcon />} onClick={handleBack} sx={{ mb: 3 }}>
      Back to IND
    </Button>
  );

  if (isLoading || projectContextLoading) {
    return (
      <ThemeProvider theme={indTheme}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (error || projectContextError) {
    return (
      <ThemeProvider theme={indTheme}>
        <Box sx={{ p: 3 }}>
          <Typography color="error" sx={{ mb: 2 }}>{error || projectContextError}</Typography>
          {backButton}
        </Box>
      </ThemeProvider>
    );
  }

  if (!section) {
    return (
      <ThemeProvider theme={indTheme}>
        <Box sx={{ p: 3 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>No section or project selected for history.</Typography>
          {backButton}
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={indTheme}>
      <Box sx={{ p: 3 }}>
        {backButton}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>{section.title}</Typography>
          <Typography variant="body1" color="text.secondary">
            IND Section History{section.documentKey ? ` - ${section.documentKey}` : ''}
          </Typography>
        </Paper>

        <List>
          {versions.length > 0 ? (
            versions.map((version, index) => (
              <React.Fragment key={version._id || index}>
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary={`Version ${version.versionNumber}`}
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.secondary" display="block" gutterBottom>
                          {version.createdAt ? new Date(version.createdAt).toLocaleString() : ''}
                        </Typography>
                        {version.prompt && (
                          <Typography component="span" variant="body2" display="block" sx={{ mt: 1 }}>
                            <strong>Prompt:</strong> {version.prompt}
                          </Typography>
                        )}
                        {version.output && (
                          <Box sx={{ mt: 1 }}>
                            <Typography component="span" variant="body2" display="block" sx={{ mb: 0.5 }}>
                              <strong>Content:</strong>
                            </Typography>
                            <Box
                              sx={{
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                p: 1,
                                fontSize: 14,
                                lineHeight: 1.5,
                                bgcolor: '#fafafa',
                              }}
                              dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(version.output) }}
                            />
                          </Box>
                        )}
                      </>
                    }
                  />
                </ListItem>
                {index < versions.length - 1 && <Divider />}
              </React.Fragment>
            ))
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No version history available for this IND section.
            </Typography>
          )}
        </List>
      </Box>
    </ThemeProvider>
  );
};

export default INDSectionHistory;
